import { useMemo } from 'react';

import { useSelector } from 'react-redux';

import { RootState } from '@/store';

import { PatientDetailsResponse } from '@/types/reportsPatient';
import { SignatureStatus } from '@/types/transmissionReport';

import { getCurrentReport } from './transmissionReportSelectors';

export const selectPatientDataById = (state: RootState, patientId: string) => {
  return state.reportsPatientApi.queries[`getReportPatientDetails("${patientId}")`]?.data as
    | PatientDetailsResponse
    | undefined;
};

export const selectPatientDetails = (state: RootState, patientId: string) => {
  const patientData = selectPatientDataById(state, patientId);
  if (!patientData) return null;

  const { patient } = patientData;

  return {
    id: patient.id,
    familyName: patient.familyName,
    givenName: patient.givenName,
    clinic: patient.clinic,
    status: patient.status,
    rhythmId: patient.rhythmId,
    mrn2: patient.mrn2 || '-',
    birthDate: patient.birthDate,
    sex: patient.sex,
    practitioner: patient.practitioner,
    signingProvider: patient.practitioner,
    inClinicPractitioner: patient.inClinicPractitioner,
    matchDevices: patient.matchDevices,
    referringProvider: patient.referringProvider,
    implantingEp: patient.implantingEp,
    implantingEpFreeText: patient.implantingEpFreeText,
    referringPhysician: patient.referringPhysician || '-',
    diagnosisOption: patient.diagnosisOption,
    oac: patient.oac,
    oacUpdatedByUser: patient.oacUpdatedByUser,
    oacUpdatedAt: patient.oacUpdatedAt,
    icdDiagnosisCodes: patient.icdDiagnosis,
    statusReason: patient.statusReason,
    statusUpdatedAt: patient.statusUpdatedAt,
    statusStartDate: patient.statusStartDate,
    statusEndDate: patient.statusEndDate,
    icdDiagnosis: patient.icdDiagnosis,
    statusOtherReason: patient.statusOtherReason
  };
};

export const selectDevicesWithUpcomingAppointment = (state: RootState, patientId: string) => {
  const patientData = selectPatientDataById(state, patientId);

  if (!patientData) return [];

  const { patient } = patientData;

  if (!patient || !patient.matchDevices || patient.matchDevices.length === 0) {
    return [];
  }

  return patient.matchDevices.map((device) => {
    // Sort appointments by date (ascending)
    const sortedAppointments = [...device.appointments]
      .sort((a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime())
      .filter((app) => new Date(app.scheduledDate) >= new Date()); // Only future appointments

    // Get the next upcoming appointment (first in the sorted array)
    const nextAppointment = sortedAppointments.length > 0 ? sortedAppointments[0] : null;

    return {
      device: {
        id: device.id,
        type: device.type,
        nextTransmissionAt: device.nextTransmissionAt
      },
      nextAppointment: nextAppointment
        ? {
            id: nextAppointment.id,
            scheduledDate: nextAppointment.scheduledDate
          }
        : null
    };
  });
};

export const selectPatientSchedule = (
  state: RootState,
  patientId: string,
  currentDeviceId?: string
) => {
  const patientData = selectPatientDataById(state, patientId);

  if (!patientData || !currentDeviceId) return [];

  // Find the current device
  const currentDevice = patientData.patient.matchDevices.find(
    (device) => device.id === currentDeviceId
  );

  if (!currentDevice) return [];

  // Extract appointments from current device only
  const appointments = currentDevice.appointments.map((appointment) => ({
    id: appointment.id,
    scheduledDate: appointment.scheduledDate,
    type: appointment.type,
    frequency: appointment.frequency,
    status: appointment.status,
    cptCode: appointment.cptCode,
    device: {
      modelName: currentDevice.modelName,
      serialNumber: currentDevice.serialNumber,
      vendor: currentDevice.vendor,
      modelNumber: currentDevice.modelNumber
    }
  }));

  // Sort appointments by scheduled date (oldest first)
  return appointments.sort(
    (a, b) => new Date(a.scheduledDate).getTime() - new Date(b.scheduledDate).getTime()
  );
};

export const selectPatientSettings = (state: RootState, patientId: string) => {
  const patientData = selectPatientDataById(state, patientId);

  if (!patientData?.patient?.settings) return null;

  return patientData.patient.settings;
};

export const useCareTeamDetails = (patientId: string, reportId: number) => {
  const patientDetails = useSelector((state: RootState) => selectPatientDetails(state, patientId));
  const reportData = useSelector((state: RootState) => getCurrentReport(state, reportId));

  return useMemo(() => {
    if (!patientDetails || !reportData) {
      return null;
    }

    const { transmissionReport } = reportData;
    const isSigned = transmissionReport.signatureStatus === SignatureStatus.Signed;

    return {
      practitioner: isSigned
        ? transmissionReport?.patient?.practitioner
        : patientDetails.practitioner,
      clinic: patientDetails.clinic,
      signingProvider: transmissionReport.signingProvider,
      referringProvider: isSigned
        ? transmissionReport?.patient?.referringProvider
        : patientDetails.referringProvider,
      implantingEp: patientDetails.implantingEp,
      implantingEpFreeText: patientDetails.implantingEpFreeText,
      referringPhysician: isSigned
        ? transmissionReport?.patient?.referringPhysician
        : patientDetails.referringPhysician,
      inClinicPractitioner: isSigned
        ? transmissionReport?.patient?.inClinicPractitioner
        : patientDetails?.inClinicPractitioner
    };
  }, [patientDetails, reportData]);
};

export const useCareTeamDetailsForProfilePage = (patientId: string) => {
  const patientDetails = useSelector((state: RootState) => selectPatientDetails(state, patientId));

  return useMemo(() => {
    if (!patientDetails) {
      return null;
    }

    return {
      practitioner: patientDetails.practitioner,
      clinic: patientDetails.clinic,
      signingProvider: null,
      referringProvider: patientDetails.referringProvider,
      referringPhysician: patientDetails.referringPhysician,
      inClinicPractitioner: patientDetails.inClinicPractitioner
    };
  }, [patientDetails]);
};
